/* =============================================================================
   PAGE RENDERER
   -----------------------------------------------------------------------------
   Binds FACTS (js/facts.js) onto the markup. The HTML holds structure and
   non-factual copy; every number, price and promise arrives from here.

   Why bind onto existing markup instead of building the DOM in JS: this page is
   going to be ported into a Shopify theme, where the markup becomes Liquid and
   these values come from the product object. Keeping the HTML semantic and
   intact means the port is a find-and-replace of the bindings, not a rewrite.
   ========================================================================== */
(function () {
  'use strict';
  /* Config is emitted by Liquid (see sections/cartbuddy-pdp.liquid). Prices and
     variant ids come from Shopify, never from JS, so the page cannot disagree
     with checkout. */
  const FACTS = window.BB_CONFIG;
  if (!FACTS) { console.error('[bb] BB_CONFIG missing'); return; }

  /* --- placeholder ------------------------------------------------------- */
  // A missing fact must be loud. Never return an empty string or a guess.
  function placeholder(need) {
    const el = document.createElement('span');
    el.className = 'ph';
    el.dataset.need = need;
    el.textContent = 'NEED: ' + need;
    return el;
  }

  /* --- flat lookup for [data-fact] --------------------------------------- */
  // Note: price / compareAt / saveBadge are deliberately NOT bound here. Every
  // price on the page now follows the selected bundle via [data-price-*], so
  // that the price box, the CTA, the gallery badge and the sticky bar can never
  // show three different numbers at once.
  const BIND = {
    productName:        FACTS.product.name,
    sku:                FACTS.product.sku,
    lifetimeWarranty:   FACTS.addons.lifetimeWarranty,
    shippingProtection: FACTS.addons.shippingProtection,
    shipping:           FACTS.promises.shipping,
    shippingShort:      FACTS.promises.shippingShort,
    guarantee:          FACTS.promises.guarantee,
    guaranteeShort:     FACTS.promises.guaranteeShort,
    // numeric in FACTS for logic; grouped for display ('4,101' not '4101')
    reviewCount:        FACTS.social.reviewCount == null
                          ? null : Number(FACTS.social.reviewCount).toLocaleString(),
  };

  document.querySelectorAll('[data-fact]').forEach(function (el) {
    const key = el.dataset.fact;

    // checkoutUrl sets an href — writing textContent here would blow away the
    // nested <span data-fact="price"> inside the button label.
    if (key === 'checkoutUrl') { el.href = FACTS.checkoutUrl; return; }

    const value = BIND[key];
    if (value === null || value === undefined) {
      el.replaceChildren(placeholder(key));
      return;
    }
    el.textContent = value;
  });

  /* --- top bars ----------------------------------------------------------- */
  // Sale bar. The clock only appears when a real end date is set; with
  // saleEndsAt null the offer shows without a countdown rather than inventing
  // urgency that resets on refresh.
  const saleBar = document.getElementById('saleBar');
  if (FACTS.saleBar && FACTS.saleBar.text) {
    document.getElementById('saleText').textContent = FACTS.saleBar.text;
    saleBar.hidden = false;
    const clock = document.getElementById('saleClock');
    const ends = FACTS.saleBar.endsAt ? new Date(FACTS.saleBar.endsAt).getTime() : null;
    if (ends && !isNaN(ends)) {
      clock.hidden = false;
      const pad = function (n) { return String(n).padStart(2, '0'); };
      const tick = function () {
        const left = ends - Date.now();
        if (left <= 0) { clock.textContent = 'Ended'; return; }
        const h = Math.floor(left / 3600000);
        const m = Math.floor(left % 3600000 / 60000);
        const s = Math.floor(left % 60000 / 1000);
        clock.innerHTML = '<b>' + pad(h) + '</b>:<b>' + pad(m) + '</b>:<b>' + pad(s) + '</b>';
        requestAnimationFrame(function () { setTimeout(tick, 1000); });
      };
      tick();
    }
  }

  // Trust marquee. The list is rendered twice so the loop is seamless: the
  // animation translates exactly -50%, landing copy 2 where copy 1 began.
  const mqTrack = document.getElementById('marqueeTrack');
  if (FACTS.marquee && FACTS.marquee.length) {
    const rating = FACTS.social.rating;
    const items = FACTS.marquee.slice();
    if (rating && FACTS.social.reviewCount) {
      items.splice(3, 0, rating.toFixed(1) + '\u2605 \u00b7 ' + Number(FACTS.social.reviewCount).toLocaleString() + ' reviews');
    }
    const build = function () {
      const frag = document.createDocumentFragment();
      items.forEach(function (t) {
        const s = document.createElement('span');
        s.className = 'marquee__item';
        s.textContent = t;
        const d = document.createElement('span');
        d.className = 'marquee__dot';
        d.textContent = '\u2022';
        frag.append(s, d);
      });
      return frag;
    };
    mqTrack.append(build(), build());
  }

  /* --- gallery ----------------------------------------------------------- */
  const main = document.getElementById('galleryMain');
  const mainVideo = document.getElementById('galleryVideo');
  // assigned once attachAudioControl exists, further down; showImage reads it
  // at call time so the ordering is fine
  let galleryAudio = null;
  const thumbs = document.getElementById('galleryThumbs');

  // smallest rendition that still covers the frame at this pixel density
  function pickGallerySource(item) {
    const list = (item.sources || []).slice().sort(function (a, b) { return (a.width || 0) - (b.width || 0); });
    if (!list.length) return null;
    const need = 640 * (window.devicePixelRatio || 1);
    for (let i = 0; i < list.length; i++) if ((list[i].width || 0) >= need) return list[i].url;
    return list[list.length - 1].url;
  }

  function showImage(i) {
    const img = activeGallery[i];
    // An empty gallery used to throw here, and because the IIFE has no
    // try/catch that TypeError killed EVERYTHING below it — bundles, prices,
    // colorways, cart drawer, sticky bar. Guard first.
    if (!img) return;

    if (img.type === 'video' && mainVideo) {
      const src = pickGallerySource(img);
      if (src) {
        if (mainVideo.getAttribute('src') !== src) mainVideo.src = src;
        if (img.src) mainVideo.poster = img.src;
        mainVideo.hidden = false;
        main.hidden = true;
        // a hidden <img> must not keep a src: it still costs a request
        main.removeAttribute('src');
        if (galleryAudio) galleryAudio.hidden = false;
        const tryPlay = function () {
          const p = mainVideo.play();
          if (p && p.catch) p.catch(function () {});
        };
        tryPlay();
        mainVideo.addEventListener('canplay', tryPlay, { once: true });
        thumbs.querySelectorAll('button').forEach(function (b, bi) {
          b.setAttribute('aria-current', String(bi === i));
        });
        return;
      }
    }

    if (mainVideo) { mainVideo.pause(); mainVideo.hidden = true; }
    if (galleryAudio) galleryAudio.hidden = true;
    main.hidden = false;
    main.src = img.src;
    main.alt = img.alt;
    thumbs.querySelectorAll('button').forEach(function (b, bi) {
      b.setAttribute('aria-current', String(bi === i));
    });
  }

  function buildGallery(list) {
    thumbs.replaceChildren();
    list.forEach(function (img, i) {
    const li = document.createElement('li');
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.setAttribute('aria-label', 'View image ' + (i + 1));
    const thumb = new Image();
    thumb.alt = '';
    if (img.type === 'video') {
      btn.classList.add('is-video');
      btn.setAttribute('aria-label', 'Play product video');
    }
    // Deliberately NOT lazy: the thumbnail strip is above the fold, and a
    // detached <img> given loading="lazy" after its src stayed at
    // naturalWidth 0 — five of six thumbs rendered blank. Eager is correct
    // here anyway; these are the smallest images on the page.
    thumb.src = img.src;
    btn.append(thumb);
    btn.addEventListener('click', function () { showImage(i); });
    li.append(btn);
    thumbs.append(li);
    });
    // A single-image colorway has nothing to switch between; the strip would
    // just be one lonely thumbnail under its own larger copy.
    if (!list.length) { thumbs.hidden = true; activeGallery = []; return; }
    thumbs.hidden = list.length < 2;
    thumbs.style.setProperty('--thumb-count', String(list.length));
    activeGallery = list;
    showImage(0);
  }
  let activeGallery = FACTS.gallery;
  buildGallery(FACTS.gallery);

  // Feature-section images reuse gallery entries by index.
  // galleryImages, not gallery: the gallery now carries a video, and using it
  // here would shift every split image and drop a poster into an <img>
  const SPLIT_IMAGES = FACTS.galleryImages || FACTS.gallery;
  document.querySelectorAll('[data-gallery-index]').forEach(function (el) {
    const img = SPLIT_IMAGES[Number(el.dataset.galleryIndex)];
    if (!img) return;
    el.src = img.src;
    el.alt = img.alt;
  });

  /* --- features ---------------------------------------------------------- */
  const featureList = document.getElementById('featureList');
  FACTS.features.forEach(function (text) {
    const li = document.createElement('li');
    li.textContent = text;
    featureList.append(li);
  });

  /* --- colorways -------------------------------------------------------- */
  // Presentation-only, like the pattern on the owner's gripforgeshop store: the
  // Shopify product still has a single variant. Selecting a colorway with no
  // photography swaps the main image for a placeholder rather than showing the
  // other color's photo, which would misrepresent what ships.
  const colorRow = document.getElementById('colorwayRow');
  const galleryMainEl = document.getElementById('galleryMain');
  const galleryWrap = galleryMainEl.parentElement;

  function applyColorway(i) {
    const c = FACTS.colorways[i];
    colorRow.querySelectorAll('.swatchbtn').forEach(function (b, bi) {
      const on = bi === i;
      b.classList.toggle('is-selected', on);
      b.setAttribute('aria-checked', String(on));
    });

    activeColor = i;
    renderTierCards();
    applyBundle(activeTier);

    // A colorway with no tier pricing cannot be bought: dim the bundle
    // selector and say why, instead of leaving the other color's prices
    // standing next to a different product.
    const buyCol = document.querySelector('.pdp__buy');
    let pending = buyCol.querySelector('.pricing-pending');
    if (!pending) {
      pending = document.createElement('p');
      pending.className = 'ph ph--block pricing-pending';
      buyCol.insertBefore(pending, document.getElementById('bundleSelector'));
    }
    const unpriced = c.bundles === null;
    pending.hidden = !unpriced;
    if (unpriced) {
      pending.dataset.need = 'prices for the ' + c.name + ' colorway';
      pending.textContent = 'NEED: pricing for ' + c.name + '. You said this one is ' +
        'cheaper than ' + FACTS.colorways[0].name + ', but not by how much — so no ' +
        'price is shown and it cannot be added to cart yet. Send the single price ' +
        'and the 2x/3x bundle prices.';
    }
    document.getElementById('bundleSelector').classList.toggle('is-disabled', unpriced);
    document.getElementById('mainCta').classList.toggle('is-disabled', unpriced);

    // Each colorway is its own Shopify product, so the CTA must point at it.
    if (c.url) {
      document.querySelectorAll('#mainCta, .stickybuy .btn').forEach(function (a) { a.href = c.url; });
    }
    // `[]` means the same thing as null: nothing to show. Treating an empty
    // array as "I have photos" left the OTHER color's photo under this label.
    const hasOwnPhotos = Array.isArray(c.gallery) && c.gallery.length > 0;
    if (hasOwnPhotos) buildGallery(c.gallery);
    else if (c.gallery === 'default') buildGallery(FACTS.gallery);

    let ph = galleryWrap.querySelector('.gallery__missing');
    if (!hasOwnPhotos && c.gallery !== 'default') {
      galleryMainEl.style.visibility = 'hidden';
      if (!ph) {
        ph = document.createElement('div');
        ph.className = 'gallery__missing';
        ph.innerHTML = '<span class="ph ph--block" data-need="product photography for this colorway">' +
          'NEED: photography of the ' + c.name + ' colorway. Nothing is shown here rather ' +
          'than showing the other color\u2019s photo, which would misrepresent what ships.</span>';
        galleryWrap.append(ph);
      }
      ph.hidden = false;
    } else {
      galleryMainEl.style.visibility = '';
      if (ph) ph.hidden = true;
    }
  }

  FACTS.colorways.forEach(function (c, i) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'swatchbtn';
    btn.setAttribute('role', 'radio');
    btn.setAttribute('aria-checked', 'false');

    const chip = document.createElement('span');
    chip.className = 'swatchbtn__chip';
    chip.style.background = c.swatch;

    const name = document.createElement('span');
    name.className = 'swatchbtn__name';
    name.textContent = c.name;

    const note = document.createElement('span');
    note.className = 'swatchbtn__note';
    // The two colorways are priced differently, so the chip carries its own
    // "from" price. A colorway with no confirmed price gets a placeholder
    // rather than inheriting the other color's figure.
    if (c.fromPrice) {
      note.textContent = 'From ' + c.fromPrice;
    } else {
      note.append(placeholder('price for ' + c.name));
    }

    btn.append(chip, name, note);
    btn.addEventListener('click', function () { applyColorway(i); });
    colorRow.append(btn);
  });

  /* --- bundles ----------------------------------------------------------- */
  // Selecting a tier rewrites every [data-price-*] node on the page at once.
  // No arithmetic anywhere: each tier carries its own hardcoded total string.
  const bundleSelector = document.getElementById('bundleSelector');

  // Which colorway's prices are in force. A tier's label, subtitle and gifts
  // come from the base tier; its money comes from the active colorway. A
  // colorway never falls back to another color's price.
  let activeColor = FACTS.defaultColorwayIndex;
  let activeTier = FACTS.defaultBundleIndex;

  function tierFor(colorIdx, tierIdx) {
    const c = FACTS.colorways[colorIdx];
    const base = FACTS.bundles[tierIdx];
    if (!c.bundles || c.bundles === 'default') return base;
    return Object.assign({}, base, c.bundles[tierIdx]);
  }

  function renderTierCards() {
    bundleSelector.querySelectorAll('.bundle').forEach(function (card, ti) {
      const t = tierFor(activeColor, ti);
      card.querySelector('.bundle__price').textContent = t.price || '';
      const was = card.querySelector('.bundle__was');
      if (was) { was.textContent = t.compareAt || ''; was.hidden = !t.compareAt; }
      const badge = card.querySelector('.bundle__badge');
      if (badge) { badge.textContent = t.badge || ''; badge.hidden = !t.badge; }
      const sub = card.querySelector('.bundle__sub');
      if (sub) sub.textContent = t.subtitle || '';
      const gifts = card.querySelector('.bundle__gifts');
      if (gifts) gifts.hidden = !(t.gifts && t.gifts.length);
    });
  }

  function applyBundle(i) {
    activeTier = i;
    const b = tierFor(activeColor, i);

    document.querySelectorAll('[data-price-now]').forEach(function (el) {
      el.textContent = b.price;
    });

    // All three tiers now carry a compare-at read off the live store, so this
    // normally fills. The hide branch stays as a guard: if a tier ever lacks
    // one, an empty struck-through gap is better than a placeholder, because a
    // genuinely absent was-price is a legitimate state, not a lost number.
    document.querySelectorAll('[data-price-was]').forEach(function (el) {
      el.textContent = b.compareAt || '';
      el.style.display = b.compareAt ? '' : 'none';
    });

    document.querySelectorAll('[data-price-badge]').forEach(function (el) {
      el.textContent = b.priceBadge || '';
      el.hidden = !b.priceBadge;
    });

    bundleSelector.querySelectorAll('.bundle').forEach(function (card, ci) {
      card.classList.toggle('is-selected', ci === i);
    });

    // the wallet buttons must buy the pack that is actually selected
    if (typeof syncBuyForms === 'function') syncBuyForms();
  }

  FACTS.bundles.forEach(function (b, i) {
    const card = document.createElement('label');
    card.className = 'bundle' + (b.highlight ? ' bundle--highlight' : '');

    const input = document.createElement('input');
    input.type = 'radio';
    input.name = 'bundle';
    input.value = String(i);
    input.className = 'bundle__radio';
    input.addEventListener('change', function () { applyBundle(i); });

    let giftBlock = null;
    const main = document.createElement('span');
    main.className = 'bundle__main';
    const label = document.createElement('span');
    label.className = 'bundle__label';
    label.textContent = b.label;
    const sub = document.createElement('span');
    sub.className = 'bundle__sub';
    sub.textContent = b.subtitle;
    main.append(label, sub);

    // Free gifts attached to this tier, with the icon the live store uses.
    if (b.gifts && b.gifts.length) {
      const gifts = document.createElement('span');
      gifts.className = 'bundle__gifts';
      b.gifts.forEach(function (g) {
        const row = document.createElement('span');
        row.className = 'bundle__gift';
        // remove() on a node that was never appended does nothing, so the
        // src-less <img> used to get appended anyway and 404 as an image.
        // Build the icon only when there is a real URL to give it.
        const t = document.createElement('span');
        t.textContent = g.text;
        if (g.icon) {
          const icon = new Image();
          icon.src = g.icon;
          icon.alt = '';
          icon.className = 'bundle__gift-icon';
          row.append(icon, t);
        } else {
          row.append(t);
        }
        gifts.append(row);
      });
      card.dataset.hasGifts = 'true';
      giftBlock = gifts;
    }

    const priceWrap = document.createElement('span');
    priceWrap.className = 'bundle__pricewrap';
    const price = document.createElement('span');
    price.className = 'bundle__price';
    price.textContent = b.price;
    priceWrap.append(price);
    if (b.compareAt) {
      const was = document.createElement('span');
      was.className = 'bundle__was';
      was.textContent = b.compareAt;
      priceWrap.append(was);
    }

    const badge = document.createElement('span');
    badge.className = 'bundle__badge';
    badge.textContent = b.badge;

    card.append(input, main, priceWrap, badge);
    // Appended last so it occupies the card's full-width second grid row —
    // inside the text column the gift labels wrapped mid-phrase at 375px.
    if (giftBlock) card.append(giftBlock);
    bundleSelector.append(card);

    if (i === FACTS.defaultBundleIndex) input.checked = true;
  });
  applyBundle(FACTS.defaultBundleIndex);
  // Runs last: it re-prices the tier cards, which must exist first.
  applyColorway(FACTS.defaultColorwayIndex);

  /* --- accelerated checkout forms ---------------------------------------- */
  // Shopify renders the real Shop Pay / PayPal / Apple Pay / Google Pay buttons
  // from {{ form | payment_button }}. There is one form per PRODUCT because Sand
  // is its own product — a wallet form can only check out a variant of its own
  // product, so showing the wrong one would check out the wrong colour.
  function syncBuyForms() {
    const tier = tierFor(activeColor, activeTier);
    const c = FACTS.colorways[activeColor];
    const variantId = tier.variantId || c.variantId;
    const want = activeColor === 0 ? 'a' : 'b';
    document.querySelectorAll('[data-color-form]').forEach(function (box) {
      const mine = box.dataset.colorForm === want;
      box.hidden = !mine;
      if (mine) {
        const input = box.querySelector('[data-variant-input]');
        if (input && variantId) input.value = variantId;
      }
    });
  }
  syncBuyForms();

  /* --- add-on icons ------------------------------------------------------ */
  document.querySelectorAll('[data-addon-icon]').forEach(function (img) {
    const u = FACTS.addons[img.dataset.addonIcon];
    if (u) { img.src = u; img.hidden = false; } else { img.remove(); }
  });

  /* --- hardware ---------------------------------------------------------- */
  const hardwareList = document.getElementById('hardwareList');
  FACTS.hardware.forEach(function (name) {
    const li = document.createElement('li');
    li.className = 'hardware__item';
    li.textContent = name;
    hardwareList.append(li);
  });

  /* --- specs -------------------------------------------------------------- */
  // Any [data-spec] with no value still falls back to a loud placeholder, so a
  // fact that gets removed later can't silently vanish from the page.
  const SPEC_TEXT = {
    fitsCarts:  FACTS.specs.fitsCarts,
    childRange: (FACTS.specs.childAgeRange && FACTS.specs.childWeightRange)
      ? FACTS.specs.childAgeRange + ' · ' + FACTS.specs.childWeightRange +
        (FACTS.specs.childHeightRange ? ' · ' + FACTS.specs.childHeightRange : '')
      : null,
    installTime: FACTS.specs.installTime,
    fitFull: (FACTS.specs.childAgeRange && FACTS.specs.childWeightRange)
      ? 'Built for ' + FACTS.specs.childAgeRange + '. Weight ' +
        FACTS.specs.childWeightRange + ', height ' + FACTS.specs.childHeightRange + '.'
      : null,
  };
  document.querySelectorAll('[data-spec]').forEach(function (el) {
    const v = SPEC_TEXT[el.dataset.spec];
    if (v) el.textContent = v; else el.replaceChildren(placeholder(el.dataset.spec));
  });

  /* --- rating ------------------------------------------------------------- */
  // No star rating is published on the live store. Rendering five gold stars
  // here would be inventing a number, so the gap shows instead.
  const ratingSlot = document.getElementById('ratingSlot');
  if (FACTS.social.rating === null) {
    ratingSlot.append(placeholder('star rating'));
  } else {
    // Five stars, filled to the rating. The half star uses a clipped overlay
    // rather than a separate glyph so 4.5 reads as exactly half, not "nearly".
    const r = FACTS.social.rating;
    const wrap = document.createElement('span');
    wrap.className = 'stars';
    wrap.setAttribute('role', 'img');
    wrap.setAttribute('aria-label', r + ' out of 5 stars');
    const STAR = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 2l2.9 6.3 6.8.8-5 4.7 1.3 6.8L12 17.3 6 20.6l1.3-6.8-5-4.7 6.8-.8z"/></svg>';
    wrap.innerHTML =
      '<span class="stars__base">' + STAR.repeat(5) + '</span>' +
      '<span class="stars__fill" style="width:' + (r / 5 * 100) + '%">' + STAR.repeat(5) + '</span>';
    ratingSlot.append(wrap);
    const num = document.createElement('span');
    num.className = 'stars__num';
    num.textContent = r.toFixed(1);
    ratingSlot.append(num);
  }

  /* --- stock line -------------------------------------------------------- */
  const stockLine = document.getElementById('stockLine');
  stockLine.textContent = FACTS.product.inStock
    ? 'In stock · ships within 24 hours'
    : 'Currently out of stock';

  /* --- video audio control ------------------------------------------------
     Clips autoplay muted and loop (browsers only autoplay MUTED video, so they
     have to start that way). This adds a speaker toggle plus a volume slider in
     the top-right corner. Unmuting one clip mutes every other one — two
     soundtracks over each other is worse than none. */
  const ALL_AUDIO_VIDEOS = [];

  function attachAudioControl(videoEl, mountEl) {
    const wrap = document.createElement('div');
    wrap.className = 'vaudio';
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'vaudio__btn';
    const slider = document.createElement('input');
    slider.type = 'range';
    slider.className = 'vaudio__range';
    slider.min = '0'; slider.max = '1'; slider.step = '0.05'; slider.value = '1';
    slider.setAttribute('aria-label', 'Volume');

    const MUTED = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M4 9h4l5-4v14l-5-4H4z" fill="currentColor"/><path d="M16 9l5 6M21 9l-5 6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';
    const LOUD  = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M4 9h4l5-4v14l-5-4H4z" fill="currentColor"/><path d="M16.5 8.5a5 5 0 0 1 0 7M19 6a8.5 8.5 0 0 1 0 12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';

    function paint() {
      const on = !videoEl.muted && videoEl.volume > 0;
      btn.innerHTML = on ? LOUD : MUTED;
      btn.setAttribute('aria-label', on ? 'Mute video' : 'Unmute video');
      btn.setAttribute('aria-pressed', String(on));
      wrap.classList.toggle('is-on', on);
      slider.value = String(videoEl.muted ? 0 : videoEl.volume);
    }
    function soloAudio() {
      ALL_AUDIO_VIDEOS.forEach(function (o) {
        if (o.el !== videoEl) { o.el.muted = true; o.paint(); }
      });
    }
    btn.addEventListener('click', function (e) {
      e.stopPropagation();
      if (videoEl.muted || videoEl.volume === 0) {
        videoEl.muted = false;
        if (videoEl.volume === 0) videoEl.volume = 1;
        soloAudio();
      } else { videoEl.muted = true; }
      paint();
    });
    slider.addEventListener('input', function (e) {
      e.stopPropagation();
      const v = Number(slider.value);
      videoEl.volume = v;
      videoEl.muted = v === 0;
      if (v > 0) soloAudio();
      paint();
    });
    // the gallery frame swaps slides on click; the control must not trigger it
    wrap.addEventListener('click', function (e) { e.stopPropagation(); });

    wrap.append(btn, slider);
    mountEl.append(wrap);
    ALL_AUDIO_VIDEOS.push({ el: videoEl, paint: paint });
    paint();
    return wrap;
  }

  if (mainVideo) {
    galleryAudio = attachAudioControl(mainVideo, mainVideo.parentElement);
    galleryAudio.hidden = mainVideo.hidden;   // gallery opens on a still
  }

  /* --- video ------------------------------------------------------------- */
  const videoSlot = document.getElementById('videoSlot');
  if (!FACTS.videos || !FACTS.videos.length) {
    const box = document.createElement('p');
    box.className = 'ph ph--block';
    box.dataset.need = 'demo video';
    box.textContent = 'PLACEHOLDER — no demo video yet.';
    videoSlot.append(box);
  } else {
    const grid = document.createElement('div');
    grid.className = 'videogrid';
    FACTS.videos.forEach(function (v) {
      const fig = document.createElement('figure');
      fig.className = 'videocard';
      const el = document.createElement('video');
      el.src = v.src;
      if (v.poster) el.poster = v.poster;
      // Muted + inline + loop is what makes these play on iOS without a tap.
      // preload=metadata so three 1080p clips don't pull megabytes on load.
      el.muted = true; el.loop = true; el.playsInline = true;
      el.autoplay = true; el.preload = 'metadata';
      el.setAttribute('aria-label', v.caption || 'CartBuddy demo video');
      fig.append(el);
      attachAudioControl(el, fig);
      if (v.caption) {
        const cap = document.createElement('figcaption');
        cap.className = 'videocard__cap';
        cap.textContent = v.caption;
        fig.append(cap);
      }
      grid.append(fig);
    });
    videoSlot.append(grid);

    // Only play what's on screen — three autoplaying clips otherwise run the
    // whole time the page is open and chew battery on a phone.
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          const v = e.target;
          if (e.isIntersecting) { const p = v.play(); if (p) p.catch(function(){}); }
          else v.pause();
        });
      }, { threshold: 0.25 });
      grid.querySelectorAll('video').forEach(function (v) { io.observe(v); });
    }
  }

  /* --- reviews ----------------------------------------------------------- */
  // No review text supplied. Writing testimonials would be fabricating customer
  // statements — the one kind of invented fact that is also a legal problem.
  const reviewsSlot = document.getElementById('reviewsSlot');
  const reviewSummary = document.getElementById('reviewSummary');

  if (FACTS.social.rating !== null) {
    const wrap = document.createElement('div');
    wrap.className = 'reviewsum__inner';
    const big = document.createElement('span');
    big.className = 'reviewsum__num';
    big.textContent = FACTS.social.rating.toFixed(1);
    const right = document.createElement('span');
    right.className = 'reviewsum__right';
    const st = document.createElement('span');
    st.className = 'reviewsum__stars';
    st.setAttribute('aria-label', FACTS.social.rating + ' out of 5 stars');
    st.textContent = '★★★★★';
    const meta = document.createElement('span');
    meta.className = 'reviewsum__meta';
    meta.textContent = FACTS.social.reviewCount
      ? Number(FACTS.social.reviewCount).toLocaleString() + ' reviews'
      : FACTS.reviews.length + ' reviews';
    right.append(st, meta);
    wrap.append(big, right);
    reviewSummary.append(wrap);
  }

  if (!FACTS.reviews || !FACTS.reviews.length) {
    const box = document.createElement('p');
    box.className = 'ph ph--block';
    box.dataset.need = 'real customer reviews';
    box.textContent = 'PLACEHOLDER — no review text supplied.';
    reviewsSlot.append(box);
  } else {
    // Auto-scrolling shelf. The card set is rendered TWICE and the track
    // translates exactly -50%, so copy 2 lands where copy 1 began and the loop
    // has no visible seam. The second copy is aria-hidden — a screen reader
    // should hear six reviews, not twelve.
    const viewport = document.createElement('div');
    viewport.className = 'reviewrow';
    const track = document.createElement('div');
    track.className = 'reviewrow__track';

    function buildCard(r) {
      const card = document.createElement('figure');
      card.className = 'rcard';
      if (r.avatar) {
        const ph = document.createElement('div');
        ph.className = 'rcard__photo';
        const im = new Image(); im.src = r.avatar; im.alt = ''; im.loading = 'lazy';
        ph.append(im); card.append(ph);
      }
      const body = document.createElement('div');
      body.className = 'rcard__body';
      const st = document.createElement('span');
      st.className = 'rcard__stars';
      st.setAttribute('aria-label', r.stars + ' out of 5 stars');
      st.textContent = '\u2605\u2605\u2605\u2605\u2605'.slice(0, r.stars);
      const q = document.createElement('blockquote');
      q.className = 'rcard__quote';
      q.textContent = r.body;
      const foot = document.createElement('figcaption');
      foot.className = 'rcard__foot';
      const nm = document.createElement('span');
      nm.className = 'rcard__name';
      nm.textContent = r.author;
      const vb = document.createElement('span');
      vb.className = 'rcard__verified';
      vb.textContent = '\u2713 Verified buyer';
      foot.append(nm, vb);
      body.append(st, q, foot);
      card.append(body);
      return card;
    }

    [0, 1].forEach(function (copy) {
      const set = document.createElement('div');
      set.className = 'reviewrow__set';
      if (copy === 1) set.setAttribute('aria-hidden', 'true');
      FACTS.reviews.forEach(function (r) { set.append(buildCard(r)); });
      track.append(set);
    });

    // Duration scales with card count so the speed reads the same whether
    // there are six reviews or sixty.
    track.style.setProperty('--marquee-duration', (FACTS.reviews.length * 7) + 's');
    viewport.append(track);
    reviewsSlot.append(viewport);
  }

  /* --- sticky mobile buy bar --------------------------------------------- */
  // Shown only once the real CTA has scrolled out of view, so there are never
  // two competing buy buttons on screen at once.
  const stickyBuy = document.getElementById('stickyBuy');
  const mainCta = document.getElementById('mainCta');
  if ('IntersectionObserver' in window) {
    // No rootMargin. An earlier '-40%' bottom margin meant the CTA counted as
    // "out of view" while it was still on screen at page load, so the sticky bar
    // rendered on top of the real button — two buy buttons, one covering the
    // other. Plain intersection is the correct rule: bar appears only once the
    // real CTA has genuinely left the viewport.
    new IntersectionObserver(function (entries) {
      stickyBuy.hidden = entries[0].isIntersecting;
    }).observe(mainCta);
  }


  /* --- cart (Ajax API) ----------------------------------------------------- */
  /* Everything here asserts against the SERVER cart. A visual change is not
     proof: /cart.js is the only truth. update.js is used rather than change.js
     because change.js addresses lines by position and breaks on reorder. */
  const Cart = {
    get: function () { return fetch('/cart.js', {headers:{'Accept':'application/json'}}).then(function(r){return r.json();}); },
    add: function (variantId, qty) {
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: {'Content-Type':'application/json','Accept':'application/json'},
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: qty || 1 }] })
      }).then(function (r) { return r.json().then(function (j) {
        if (!r.ok) throw new Error(j.description || j.message || ('cart/add ' + r.status));
        return j; }); });
    },
    // absolute quantity by variant id, never by line position
    setQty: function (variantId, qty) {
      const u = {}; u[String(variantId)] = qty;
      return fetch('/cart/update.js', {
        method: 'POST',
        headers: {'Content-Type':'application/json','Accept':'application/json'},
        body: JSON.stringify({ updates: u })
      }).then(function (r) { return r.json(); });
    }
  };
  window.BB_CART = Cart;

  function money(cents) {
    // formatting only — never arithmetic on a displayed price
    return (FACTS.moneyFormat || '${{amount}}').replace('{{amount}}', (cents / 100).toFixed(2));
  }

  function refreshCartCount() {
    return Cart.get().then(function (c) {
      const el = document.getElementById('cartCount');
      if (el) el.textContent = String(c.item_count);
      return c;
    });
  }

  /* --- cart drawer -------------------------------------------------------- */
  /* Mirrors gripforgeshop's drawer. Everything here is presentation: there is
     no real cart. Shopify owns the cart on the theme port.

     THE SUBTOTAL IS NEVER COMPUTED. It shows the selected tier's own hardcoded
     total string. Add-ons are listed with their own hardcoded prices but are
     deliberately NOT summed in — adding $4.99 + $3.99 to a bundle price in JS
     is exactly the arithmetic that drifts a cent from what Shopify charges.
     The note under the subtotal says so plainly. */
  const drawer = document.getElementById('cartDrawer');
  const scrim  = document.getElementById('cartScrim');
  // Both add-ons are real Shopify products with real variants, so ticking one
  // puts an actual line in the cart. A checkbox that only changed local state
  // would promise a charge that never happens.
  const UPSELLS = [
    { name: 'Lifetime warranty',  note: 'Free replacement, forever',
      price: FACTS.addons.lifetimeWarranty,   icon: FACTS.addons.lifetimeWarrantyIcon,
      variantId: FACTS.addons.lifetimeWarrantyVariantId },
    { name: 'Shipping protection', note: 'Loss, theft & damage',
      price: FACTS.addons.shippingProtection, icon: FACTS.addons.shippingProtectionIcon,
      variantId: FACTS.addons.shippingProtectionVariantId },
  ];

  // Shopify hands back integer cents; substitute into the shop's own money
  // format rather than assuming a currency symbol.
  function money(cents) {
    const amount = (Number(cents) / 100).toFixed(2);
    const fmt = FACTS.moneyFormat || '';
    return /\{\{\s*amount\s*\}\}/.test(fmt)
      ? fmt.replace(/\{\{\s*amount\s*\}\}/, amount)
      : '$' + amount;
  }

  // The add-on rows on the PAGE are the same two real products as the ones in
  // the drawer, so both sets of checkboxes are driven from, and write to, the
  // one real cart. They used to be inert controls advertising a charge that
  // never happened.
  const ADDON_VARIANTS = {
    warranty:   FACTS.addons.lifetimeWarrantyVariantId,
    protection: FACTS.addons.shippingProtectionVariantId
  };

  function syncAddonBoxes(cart) {
    const inCart = {};
    (cart && cart.items || []).forEach(function (i) { inCart[i.variant_id] = i.quantity; });
    document.querySelectorAll('.addon[data-addon]').forEach(function (row) {
      const id = ADDON_VARIANTS[row.dataset.addon];
      const box = row.querySelector('.addon__box');
      if (!box) return;
      if (!id) { box.disabled = true; return; }
      box.checked = !!inCart[id];
    });
  }

  function wireAddonBoxes() {
    document.querySelectorAll('.addon[data-addon]').forEach(function (row) {
      const id = ADDON_VARIANTS[row.dataset.addon];
      const box = row.querySelector('.addon__box');
      if (!box) return;
      if (!id) { box.disabled = true; return; }
      box.addEventListener('change', function () {
        box.disabled = true;
        // absolute quantity by variant id — 1 to add, 0 to remove
        Cart.setQty(id, box.checked ? 1 : 0)
          .then(function () { return Cart.get(); })
          .then(function (cart) { syncAddonBoxes(cart); return refreshCartCount(); })
          .then(function () { if (drawer.classList.contains('is-open')) renderCart(); })
          .catch(function (err) {
            console.error('[bb] add-on update failed', err);
            box.checked = !box.checked;
          })
          .finally(function () { box.disabled = false; });
      });
    });
    Cart.get().then(syncAddonBoxes).catch(function () {});
  }
  wireAddonBoxes();

  function openCart(open) {
    drawer.classList.toggle('is-open', open);
    drawer.setAttribute('aria-hidden', String(!open));
    scrim.hidden = !open;
    document.getElementById('cartToggle').setAttribute('aria-expanded', String(open));
    document.body.style.overflow = open ? 'hidden' : '';
    if (open) renderCart();
  }
  document.getElementById('cartToggle').addEventListener('click', function(){ openCart(!drawer.classList.contains('is-open')); });
  document.getElementById('cartClose').addEventListener('click', function(){ openCart(false); });
  scrim.addEventListener('click', function(){ openCart(false); });
  addEventListener('keydown', function(e){ if (e.key === 'Escape') openCart(false); });

  // Maps a variant id back to the colour/tier it belongs to, so the cart can
  // be read even when the shopper is looking at the other colourway.
  function tierOfVariant(variantId) {
    for (let ci = 0; ci < FACTS.colorways.length; ci++) {
      const list = (FACTS.colorways[ci].bundles === 'default') ? FACTS.bundles : FACTS.colorways[ci].bundles;
      if (!Array.isArray(list)) continue;
      for (let ti = 0; ti < list.length; ti++) {
        if (String(list[ti].variantId) === String(variantId)) return { colorIdx: ci, tierIdx: ti, tier: list[ti] };
      }
    }
    return null;
  }

  function seatsInCart(cart) {
    let seats = 0, colorIdx = null;
    (cart.items || []).forEach(function (item) {
      const hit = tierOfVariant(item.variant_id);
      if (!hit) return;                       // add-ons are not seats
      seats += (hit.tier.seats || 1) * item.quantity;
      if (colorIdx === null) colorIdx = hit.colorIdx;
    });
    return { seats: seats, colorIdx: colorIdx === null ? activeColor : colorIdx };
  }

  function renderGiftProgress(cart) {
    const gp = document.getElementById('giftProgress');
    if (!gp) return;

    const info = seatsInCart(cart);
    const seats = info.seats;
    const tiers = [0, 1, 2].map(function (i) { return tierFor(info.colorIdx, i); });
    const cents = tiers.map(function (t) { return t ? Number(t.priceCents) : null; });

    const gap = function (from, to) {
      if (cents[from] == null || cents[to] == null) return null;
      return money(cents[to] - cents[from]);
    };

    // What each threshold actually unlocks. Tier index 1 is the 2-seat set,
    // index 2 the 3-seat set, so the seats needed is always index + 1. The
    // gift wording is the tier's own copy — nothing is invented here.
    const gifts = [];
    [1, 2].forEach(function (ti) {
      const t = tiers[ti];
      if (!t) return;
      (t.gifts || []).forEach(function (g) {
        gifts.push({
          text: String(g.text || '').replace(/^\+\s*/, ''),
          needed: ti + 1,
          unlocked: seats >= (ti + 1)
        });
      });
    });
    const unlockedCount = gifts.filter(function (g) { return g.unlocked; }).length;
    const nextTierGifts = gifts.filter(function (g) { return !g.unlocked && g.needed === (seats >= 2 ? 3 : 2); }).length;

    let msg, done = false;
    if (seats >= 3) {
      msg = '<strong>All ' + gifts.length + ' free gifts unlocked</strong> \u2014 best price per seat';
      done = true;
    } else if (seats === 2) {
      const more = gap(1, 2);
      msg = '<strong>' + unlockedCount + ' free gifts unlocked</strong>' +
            (more ? ' \u2014 ' + more + ' more for ' + nextTierGifts + ' more' : '');
    } else if (seats === 1) {
      const away = gap(0, 1);
      msg = (away ? '<strong>' + away + '</strong> away from a ' : 'Add a seat for a ') + 'FREE 5 Year Warranty';
    } else {
      msg = 'Add a seat to start unlocking free gifts';
    }

    // One row per THRESHOLD, not per gift: both of a tier's gifts arrive
    // together, so listing them separately just doubled the rows.
    const byThreshold = {};
    gifts.forEach(function (g) {
      (byThreshold[g.needed] = byThreshold[g.needed] || []).push(g);
    });
    const giftList = Object.keys(byThreshold).sort().map(function (need) {
      const group = byThreshold[need];
      const unlocked = group[0].unlocked;
      const names = group.map(function (g) {
        return '+ FREE ' + g.text.replace(/^FREE\s+/i, '');
      }).join(' ');
      return '<li class="giftprog__gift' + (unlocked ? ' is-on' : '') + '">' +
               '<span class="giftprog__tick" aria-hidden="true">' + (unlocked ? '\u2713' : '') + '</span>' +
               '<span class="giftprog__giftname">' + names + '</span>' +
               (unlocked ? '' : '<span class="giftprog__need">at ' + need + ' seats</span>') +
             '</li>';
    }).join('');

    const pct = Math.min(100, Math.round((Math.min(seats, 3) / 3) * 100));

    gp.innerHTML =
      '<p class="giftprog__msg' + (done ? ' is-done' : '') + '">' +
        '<span class="giftprog__icon" aria-hidden="true">' + (done ? '\u{1F381}' : (seats >= 2 ? '\u2713' : '')) + '</span>' +
        msg +
      '</p>' +
      '<div class="giftprog__track" role="progressbar" aria-valuemin="0" aria-valuemax="3" aria-valuenow="' + seats + '">' +
        '<span class="giftprog__fill" style="width:' + pct + '%"></span>' +
      '</div>' +
      '<div class="giftprog__marks">' +
        '<span class="' + (seats >= 2 ? 'is-hit' : '') + '">2 seats</span>' +
        '<span class="' + (seats >= 3 ? 'is-hit' : '') + '">3 seats</span>' +
      '</div>' +
      '<ul class="giftprog__list">' + giftList + '</ul>' +
      '<p class="giftprog__ship"><span aria-hidden="true">\u2713</span> FREE shipping included on your order</p>';
  }

  // THE DRAWER RENDERS THE CART, NOT THE PAGE'S SELECTION.
  // It used to draw a line from whatever tier/colour was selected, so adding
  // Charcoal and then clicking Sand made the drawer show Sand while the cart
  // held Charcoal. Every number below comes from /cart.js.
  // Renders can overlap: every stepper click starts one, and responses do not
  // come back in the order they were sent. Without this guard the SLOWER, older
  // response paints last and the drawer shows a quantity and price the cart
  // does not have. Each call takes a ticket; only the newest is allowed to paint.
  let renderTicket = 0;

  function renderCart() {
    const ticket = ++renderTicket;
    return Cart.get().then(function (cart) {
      if (ticket !== renderTicket) return cart;   // superseded — drop this paint
      document.getElementById('cartCount').textContent = String(cart.item_count);
      document.getElementById('cartLineCount').textContent =
        cart.item_count + (cart.item_count === 1 ? ' item' : ' items');

      const lines = document.getElementById('cartLines');
      lines.replaceChildren();

      if (!cart.items.length) {
        const empty = document.createElement('li');
        empty.className = 'cartline cartline--empty';
        empty.textContent = 'Your cart is empty.';
        lines.append(empty);
      }

      cart.items.forEach(function (item) {
        const li = document.createElement('li');
        li.className = 'cartline';
        if (item.image) {
          const img = new Image();
          img.className = 'cartline__img';
          img.src = item.image;
          img.alt = '';
          li.append(img);
        }
        const body = document.createElement('div');
        body.className = 'cartline__body';
        const nm = document.createElement('span');
        nm.className = 'cartline__name';
        nm.textContent = item.product_title;
        const meta = document.createElement('span');
        meta.className = 'cartline__meta';
        // variant_title is "Default Title" on single-variant products
        const variantBit = item.variant_title && item.variant_title !== 'Default Title'
          ? item.variant_title : '';
        meta.textContent = [variantBit, 'Qty ' + item.quantity].filter(Boolean).join(' · ');
        body.append(nm, meta);
        (item.discounts || []).forEach(function (d) {
          const dd = document.createElement('span');
          dd.className = 'cartline__discount';
          dd.textContent = d.title;
          body.append(dd);
        });
        // quantity stepper + remove, same controls as the cart page.
        // Writes go through /cart/update.js (absolute quantity by variant id);
        // /cart/change.js addresses lines by POSITION and edits the wrong row
        // once something above it has been removed.
        const ctrl = document.createElement('div');
        ctrl.className = 'cartline__ctrl';

        const step = document.createElement('div');
        step.className = 'cartline__stepper';
        const mk = function (label, delta, aria) {
          const b = document.createElement('button');
          b.type = 'button'; b.className = 'cartline__step'; b.textContent = label;
          b.setAttribute('aria-label', aria);
          b.addEventListener('click', function () {
            const next = Math.max(0, item.quantity + delta);
            ctrl.querySelectorAll('button').forEach(function (x) { x.disabled = true; });
            Cart.setQty(item.variant_id, next)
              .then(renderCart)
              .then(refreshCartCount)
              .catch(function (err) { console.error('[bb] cart update failed', err); })
              .finally(function () { ctrl.querySelectorAll('button').forEach(function (x) { x.disabled = false; }); });
          });
          return b;
        };
        const qty = document.createElement('span');
        qty.className = 'cartline__qty';
        qty.textContent = String(item.quantity);
        // at quantity 1 the decrement IS a removal, so it shows a bin rather
        // than a minus that would silently empty the line
        const down = mk(item.quantity === 1 ? '\u{1F5D1}' : '\u2212', -1,
                        item.quantity === 1 ? 'Remove item' : 'Decrease quantity');
        if (item.quantity === 1) down.classList.add('cartline__step--bin');
        step.append(down, qty, mk('+', 1, 'Increase quantity'));

        const rm = document.createElement('button');
        rm.type = 'button'; rm.className = 'cartline__remove';
        rm.setAttribute('aria-label', 'Remove ' + item.product_title);
        rm.innerHTML = '<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">' +
          '<path d="M4 7h16M9 7V5h6v2m-8 0 1 13h8l1-13" fill="none" stroke="currentColor" ' +
          'stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"/></svg>';
        rm.addEventListener('click', function () {
          rm.disabled = true;
          Cart.setQty(item.variant_id, 0)
            .then(renderCart)
            .then(refreshCartCount)
            .catch(function (err) { console.error('[bb] remove failed', err); rm.disabled = false; });
        });

        ctrl.append(step, rm);

        const pr = document.createElement('span');
        pr.className = 'cartline__price';
        pr.textContent = money(item.final_line_price);
        body.append(ctrl);
        li.append(body, pr);
        lines.append(li);
      });

      // ---- gift progress, driven by the REAL cart -----------------------
      // Counts SEATS, not lines: a 3-seat pack at quantity 1 is three seats.
      // The dollar figures are deltas between two Shopify prices, never a
      // charged amount, so they cannot disagree with checkout.
      renderGiftProgress(cart);

      // Add-ons: checked state reflects whether the line is actually IN the cart.
      const inCart = {};
      cart.items.forEach(function (i) { inCart[i.variant_id] = i.quantity; });
      const ul = document.getElementById('upsellList');
      ul.replaceChildren();
      UPSELLS.forEach(function (u) {
        const lab = document.createElement('label');
        lab.className = 'upsell';
        lab.innerHTML =
          '<img class="upsell__icon" alt=""><span class="upsell__body">' +
          '<span class="upsell__name"></span><span class="upsell__note"></span></span>' +
          '<span class="upsell__price"></span><input type="checkbox" class="upsell__box">';
        // an empty src resolves to the page URL and 404s as an image — drop the node instead
        const ico = lab.querySelector('.upsell__icon');
        if (u.icon) ico.src = u.icon; else ico.remove();
        lab.querySelector('.upsell__name').textContent = u.name;
        lab.querySelector('.upsell__note').textContent = u.note;
        lab.querySelector('.upsell__price').textContent = '+' + u.price;
        const box = lab.querySelector('.upsell__box');
        box.checked = !!inCart[u.variantId];
        if (!u.variantId) {
          // A row whose variant never resolved is dead weight, so the stylesheet
          // removes it. It is flagged with a CLASS rather than matched on
          // :disabled, because the change handler below disables the box for the
          // length of the request -- keying the rule off :disabled collapsed a
          // perfectly good row to zero height on every tick.
          lab.classList.add('upsell--unavailable');
          box.disabled = true;
        } else {
          box.addEventListener('change', function () {
            box.disabled = true;
            // absolute quantity by variant id — 1 to add, 0 to remove
            Cart.setQty(u.variantId, box.checked ? 1 : 0)
              .then(renderCart)
              .then(refreshCartCount)
              .catch(function (err) {
                console.error('[bb] add-on update failed', err);
                box.checked = !box.checked;
              })
              .finally(function () { box.disabled = false; });
          });
        }
        ul.append(lab);
      });

      syncAddonBoxes(cart);
      document.getElementById('cartShipping').textContent = 'Free';
      document.getElementById('cartSubtotal').textContent = money(cart.total_price);
      document.getElementById('cartCheckout').href = '/checkout';
      updateTotalNote(cart);
      return cart;
    }).catch(function (err) {
      console.error('[bb] could not read the cart', err);
    });
  }

  function updateTotalNote(cart) {
    const el = document.getElementById('cartTotalNote');
    if (!el) return;
    const discounted = cart && cart.items.some(function (i) { return (i.discounts || []).length; });
    el.textContent = discounted
      ? 'Subtotal is the cart total from Shopify, including discounts. Taxes calculated at checkout.'
      : 'Free U.S. shipping. Taxes calculated at checkout.';
  }

  renderCart();


  /* --- analytics ---------------------------------------------------------- */
  // Shopify does NOT auto-report a cart add on this store: a page view produces
  // ~11 tracking requests, an Ajax add produced 0 (measured on the live site,
  // both JSON and form-encoded bodies). Marketing pixels therefore never learn
  // about add-to-cart unless the theme says so. Wrapped in try/catch because an
  // analytics failure must never stop a customer buying.
  function announceAddToCart(variantId, tier, colour, res) {
    try {
      if (!(window.Shopify && Shopify.analytics && Shopify.analytics.publish)) return;
      const line = (res && res.items && res.items[0]) || {};
      const cents = typeof line.final_line_price === 'number' ? line.final_line_price : null;
      const amount = cents === null ? null : cents / 100;
      Shopify.analytics.publish('product_added_to_cart', {
        cartLine: {
          quantity: 1,
          cost: { totalAmount: { amount: amount, currencyCode: FACTS.currency || 'USD' } },
          merchandise: {
            id: 'gid://shopify/ProductVariant/' + variantId,
            title: (tier && tier.label) || '',
            untranslatedTitle: (tier && tier.label) || '',
            sku: line.sku || null,
            price: { amount: amount, currencyCode: FACTS.currency || 'USD' },
            product: {
              id: line.product_id ? 'gid://shopify/Product/' + line.product_id : null,
              title: line.product_title || FACTS.product.name,
              untranslatedTitle: line.product_title || FACTS.product.name,
              vendor: line.vendor || FACTS.brand,
              type: line.product_type || '',
              url: (colour && colour.url) || FACTS.checkoutUrl
            }
          }
        }
      });
    } catch (err) {
      console.warn('[bb] add-to-cart analytics did not publish', err);
    }
  }

  /* --- add to cart --------------------------------------------------------- */
  (function wireAddToCart() {
    // Every control that says "Add to cart" is wired here. Until now only
    // #mainCta was: the closer CTA and the sticky mobile bar were plain links
    // to /cart, so tapping either added NOTHING and dropped the shopper on an
    // empty cart page. Two of the three buttons on the page were dead.
    const btns = [].slice.call(document.querySelectorAll('[data-addcart]'));
    if (!btns.length) return;

    // Shared across every button: two different buttons must not be able to
    // fire two /cart/add.js calls and produce a line of quantity 2, which is
    // exactly what trips a quantity-break discount function. <a> has no
    // .disabled, so this boolean is the only real guard.
    let inFlight = false;

    btns.forEach(function (btn) {
      // The price inside the button lives in a <span data-price-now> that
      // applyBundle() rewrites on every colour/tier change. Writing
      // btn.textContent destroys that span, so the button would freeze on
      // whatever price was showing at the first click while the rest of the
      // page kept updating -- advertising one price while the cart charges
      // another. Mutate a dedicated status node; never touch the children.
      let status = btn.querySelector('.btn__status');
      if (!status) {
        status = document.createElement('span');
        status.className = 'btn__status';
        status.hidden = true;
        btn.append(status);
      }
      const face = btn.querySelector('.btn__face') || (function () {
        const f = document.createElement('span');
        f.className = 'btn__face';
        while (btn.firstChild !== status) f.append(btn.firstChild);
        btn.insertBefore(f, status);
        return f;
      })();

      function setStatus(msg) {
        if (msg) { status.textContent = msg; status.hidden = false; face.hidden = true; }
        else { status.hidden = true; face.hidden = false; }
      }

      btn.addEventListener('click', function (e) {
        e.preventDefault();
        if (inFlight) return;
        const c = FACTS.colorways[activeColor];
        const tier = tierFor(activeColor, activeTier);
        const variantId = tier.variantId || c.variantId;
        if (!variantId) { console.error('[bb] no variant id for', c.name, tier.label); return; }
        inFlight = true;
        btns.forEach(function (b) { b.setAttribute('aria-disabled', 'true'); });
        setStatus('Adding\u2026');
        // One line, quantity 1 -- never "3 x quantity 1".
        Cart.add(variantId, 1)
          .then(function (res) { announceAddToCart(variantId, tier, c, res); return refreshCartCount(); })
          .then(function () {
            // Straight to checkout. The drawer is deliberately NOT opened --
            // the owner asked for the shortest path from button to payment.
            // announceAddToCart has already published by this point, so the
            // analytics event is queued before the page is torn down.
            setStatus('Taking you to checkout\u2026');
            window.location.assign(FACTS.checkoutNowUrl || '/checkout');
          })
          .catch(function (err) {
            console.error('[bb] add to cart failed', err);
            setStatus('Could not add \u2014 try again');
            setTimeout(function () { setStatus(null); }, 2500);
            inFlight = false;
            btns.forEach(function (b) { b.removeAttribute('aria-disabled'); });
          });
        // Deliberately no .finally() clearing inFlight on success: the
        // navigation IS the end state. Releasing the guard would let a second
        // click fire another add during the redirect and double the line.
      });
    });
  })();

  refreshCartCount();

  /* --- load diagnostics -------------------------------------------------- */
  // Surfaces a failed hotlink in the console. Verification checks naturalWidth
  // directly; this just makes a broken CDN URL obvious while iterating.
  window.addEventListener('load', function () {
    // Only flag images that finished loading with no pixels. A lazy image that
    // is still offscreen also reports naturalWidth 0 and is not a failure —
    // treating those as broken produces noise that hides the real ones.
    const broken = [...document.images].filter(function (i) { return i.complete && i.naturalWidth === 0; });
    if (broken.length) console.error('[bb] images failed to load:', broken.map(function (i) { return i.src; }));
    // Wait for a frame and require a real layout before judging overflow. Run
    // at `load` alone this fired with clientWidth 0 — a zero-sized viewport
    // reports every element as overflowing, which is a false alarm that trains
    // you to ignore the true one.
    requestAnimationFrame(function () {
      const doc = document.documentElement;
      if (doc.clientWidth === 0) return;
      if (doc.scrollWidth > doc.clientWidth) {
        console.error('[bb] horizontal overflow:', doc.scrollWidth, '>', doc.clientWidth);
      }
    });
  });
})();
